import type { UserListItem } from "newui/features/Auth/types/userInvite";
import type { UserAssignment } from "newui/features/RolesPermissions/types/RolesPermissions.type";

/**
 * Tour-only dummy users.
 *
 * Negative IDs flag these rows as fixtures so action handlers can
 * short-circuit (no real API calls fire if the consumer clicks an
 * action button mid-tour). Names are prefixed "(demo)" to make their
 * purpose obvious if they're seen outside the tour.
 *
 * `TOUR_USER_INVITED` covers the resend + deactivate flows; the
 * "invited" status renders the mail and deactivate icons in the row.
 *
 * `TOUR_USER_DEACTIVATED` covers the reactivate flow; the
 * "deactivated" status renders the reactivate icon.
 */
const NOW_ISO = new Date(0).toISOString();

const PLACEHOLDER_ROLE = {
	id: -1,
	name: "team_member",
	display_name: "Team Member",
};

export const TOUR_USER_INVITED: UserListItem = {
	id: -1001,
	uuid: "tour-user-invited",
	first_name: "(demo)",
	last_name: "Pending Invite",
	email: "pending.invite@example.com",
	phone: null,
	role: PLACEHOLDER_ROLE,
	status: "invited",
	invited_at: NOW_ISO,
	invite_expires_at: NOW_ISO,
	registered_at: null,
	invited_by: null,
	deleted_at: null,
};

export const TOUR_USER_DEACTIVATED: UserListItem = {
	id: -1002,
	uuid: "tour-user-deactivated",
	first_name: "(demo)",
	last_name: "Deactivated User",
	email: "deactivated@example.com",
	phone: null,
	role: PLACEHOLDER_ROLE,
	status: "deactivated",
	invited_at: NOW_ISO,
	invite_expires_at: null,
	registered_at: NOW_ISO,
	invited_by: null,
	deleted_at: null,
};

/** All tour dummy users, in the order they should appear in the table. */
export const TOUR_USERS: ReadonlyArray<UserListItem> = [
	TOUR_USER_INVITED,
	TOUR_USER_DEACTIVATED,
];

/** Predicate — true for any row injected by the tour. */
export const isTourDummyUser = (user: { id: number }): boolean => user.id < 0;

/**
 * Tour-only "manage roles" shell for `TOUR_USER_INVITED`, used by the
 * "Manage a user's roles" topic (`manageRolesSteps`).
 *
 * `ManageRolesModal`'s table (`manage-roles-table`) has both a populated and
 * an empty state, so this carries one demo global assignment — negative
 * `assignment_id`/`role_id`, same convention as the negative user id, so it
 * can never collide with a real row and (belt and braces, alongside the
 * id<0 mutation guards in `ManageRolesModal` and `AddAccessFlow`) can never
 * be mistaken for something worth persisting.
 */
export const TOUR_USER_ASSIGNMENT: UserAssignment = {
	id: TOUR_USER_INVITED.id,
	name: `${TOUR_USER_INVITED.first_name} ${TOUR_USER_INVITED.last_name}`.trim(),
	email: TOUR_USER_INVITED.email,
	migration_status: "pending",
	assignments: [
		{
			assignment_id: -1,
			role_id: -1,
			role_name: "Team Member",
			scope_type: "global",
			scope_id: null,
			scope_label: null,
		},
	],
	flags: { template_creation: false },
};
